import type { BuildInput, CutRow, MaterialRow, RoofConfig } from '../../engines/types';
import { getRuleValue, type RuleOverrides } from '../../data/ruleRegistry';
import { generateFramedWall } from '../framing/generateFramedWall';
import type { WallOpening } from '../framing/generateFramedWall';
import { createRectRoofGeometry } from '../roof/rectRoofGeometry';
import { DEFAULT_ROOF_CONFIG_BY_TYPE } from '../roof/roofConfig';
import { resolveWallHeightM } from '../geometry/buildingDimensions';
import { clampPartitionSplit } from './partitionSplit';

type PartitionSupportWall = 'left' | 'right';

export interface PartitionWallSupportSide {
  wall: PartitionSupportWall;
  alignedToStud: boolean;
  studCenterMm?: number;
  bayStartMm?: number;
  bayEndMm?: number;
  blockingLengthMm?: number;
}

export interface PartitionWallFramingResult {
  status: 'direct-stud-anchor' | 'requires-blocking';
  dividerPositionMm: number;
  studWidthMm: number;
  requiredBlockingCount: number;
  sides: PartitionWallSupportSide[];
  materials: MaterialRow[];
  cuts: CutRow[];
}

function uniqueSorted(values: number[]) {
  return [...new Set(values)].sort((a, b) => a - b);
}

function getSideWallHeightMm(
  dimensions: BuildInput['dimensions'],
  roofConfig: RoofConfig,
  wall: PartitionSupportWall,
  wallHeightM: number,
) {
  const roofGeometry = createRectRoofGeometry({
    width: dimensions.width,
    length: dimensions.length,
    wallHeight: wallHeightM,
    roofConfig,
  });

  return Math.round(roofGeometry.getMaxWallHeight(wall) * 1000);
}

function getSideOpenings(openings: WallOpening[], wall: PartitionSupportWall) {
  return openings.filter((opening) => opening.wall === wall);
}

function describeSideSupport(
  wall: PartitionSupportWall,
  studCentersMm: number[],
  dividerPositionMm: number,
  studWidthMm: number,
): PartitionWallSupportSide {
  const directStud = studCentersMm.find((centerMm) => Math.abs(centerMm - dividerPositionMm) <= studWidthMm / 2);
  if (directStud !== undefined) {
    return {
      wall,
      alignedToStud: true,
      studCenterMm: directStud,
    };
  }

  const lowerStud = [...studCentersMm].reverse().find((centerMm) => centerMm < dividerPositionMm);
  const upperStud = studCentersMm.find((centerMm) => centerMm > dividerPositionMm);
  const blockingLengthMm = lowerStud !== undefined && upperStud !== undefined
    ? Math.max(0, upperStud - lowerStud - studWidthMm)
    : undefined;

  return {
    wall,
    alignedToStud: false,
    bayStartMm: lowerStud,
    bayEndMm: upperStud,
    blockingLengthMm,
  };
}

export function calculatePartitionWallFraming(
  input: Pick<BuildInput, 'dimensions' | 'openings' | 'partitionSplit' | 'roofConfig' | 'glazingStyle' | 'hasRelaxRoom' | 'saunaPosition' | 'wallHeightM'>,
  overrides: RuleOverrides = {},
): PartitionWallFramingResult {
  const roofConfig = input.roofConfig ?? DEFAULT_ROOF_CONFIG_BY_TYPE.tvarum;
  const studWidthMm = getRuleValue<number>('framing.studWidthMm', overrides);
  // Use the same clamped split the engine renders with, and mirror its
  // saunaPosition convention (divider measured from the back wall), so the
  // blocking lands at the actual divider position on each side wall.
  const split = clampPartitionSplit(input.dimensions.length, input.partitionSplit ?? 0.5);
  const dividerFraction = input.saunaPosition === 'right' ? 1 - split : split;
  const dividerPositionMm = Math.round(dividerFraction * input.dimensions.length * 1000);
  // Panorama builds a dedicated mullion stud at the divider on the right
  // (glass) wall, so the partition anchors directly to a stud there — no
  // compensating blocking is needed on that side.
  const panoramaAnchorsRight =
    input.glazingStyle === 'panorama' && input.hasRelaxRoom !== false;

  const sides = (['left', 'right'] as const).map((wall) => {
    if (wall === 'right' && panoramaAnchorsRight) {
      return { wall, alignedToStud: true, studCenterMm: dividerPositionMm };
    }

    const framed = generateFramedWall({
      wallLengthMm: Math.round(input.dimensions.length * 1000),
      // The same height the engine frames the side wall at (issue 155).
      wallHeightMm: getSideWallHeightMm(
        input.dimensions,
        roofConfig,
        wall,
        resolveWallHeightM(input.wallHeightM),
      ),
      openings: getSideOpenings(input.openings, wall),
    }, overrides);

    return describeSideSupport(
      wall,
      uniqueSorted(framed.studs.filter(stud => stud.type === 'standard' || stud.type === 'king_stud').map((stud) => stud.xMm)),
      dividerPositionMm,
      studWidthMm,
    );
  });

  // Without a relax room there is no partition wall at all — the anchoring
  // analysis is still returned (the slider UI reads it), but no blocking
  // lumber may be billed for a wall that is never built.
  const hasPartition = input.hasRelaxRoom !== false;
  const blockingSides = hasPartition
    ? sides.filter((side) => !side.alignedToStud && side.blockingLengthMm && side.blockingLengthMm > 0)
    : [];
  const totalBlockingLpm = Number((blockingSides.reduce((sum, side) => sum + (side.blockingLengthMm ?? 0), 0) / 1000).toFixed(2));
  const materials: MaterialRow[] = blockingSides.length > 0
    ? [{
        material: 'Kortling för mellanväggsanslutning',
        dimension: '45×95 mm',
        quantity: `${blockingSides.length} st = ${totalBlockingLpm} lpm`,
        phase: 'framing',
        priceRuleId: 'prices.kortlingRegel',
        materialKey: 'partition.blocking',
      }]
    : [];
  const cuts: CutRow[] = blockingSides.map((side) => ({
    part: `Kortling ${side.wall === 'left' ? 'vänster' : 'höger'} yttervägg (mellanväggsanslutning)`,
    count: '1 st',
    cutLength: `${Math.round(side.blockingLengthMm!)} mm`,
    materialKey: 'partition.blocking',
    purchaseDimension: '45x95',
    pieces: [{lengthMm:Math.round(side.blockingLengthMm!),count:1}],
  }));

  return {
    status: blockingSides.length > 0 ? 'requires-blocking' : 'direct-stud-anchor',
    dividerPositionMm,
    studWidthMm,
    requiredBlockingCount: blockingSides.length,
    sides,
    materials,
    cuts,
  };
}
